"use client";

import { Fragment } from "react";
import type { LanguageOption } from "../../../types";
import { useLocale } from "../../../runtime/RendererRuntime";
import { t } from "../../../i18n/strings";

/**
 * Inline language switch shown in the footer bottom bar:
 *
 *     FR · EN · LN
 *
 * Each language is a real anchor (`<a href>`) pointing at the locale-prefixed URL of
 * the current page, so it reads as a proper link — pointer cursor, right-click / open
 * in new tab, crawlable. `resolveHref(code)` (supplied by the Next.js layer) builds
 * that URL. The click is intercepted to run `onLanguageChange`, which performs a FULL
 * document navigation: the localized header/footer are rendered by the root server
 * layout, so a soft client transition would leave the chrome in the previous language.
 * The active language reads solid white; the others dim until hover. Renders nothing
 * when fewer than two languages are enabled.
 */
export function FooterLanguageLinks({
  language,
  languageOptions,
  onLanguageChange,
  resolveHref,
}: {
  language: string;
  languageOptions: LanguageOption[];
  onLanguageChange: (code: string) => void;
  resolveHref?: (code: string) => string;
}) {
  const locale = useLocale();
  const options = languageOptions ?? [];
  if (options.length < 2) return null;

  return (
    <nav
      aria-label={t(locale, "changeLanguage")}
      className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide"
    >
      {options.map((option, index) => {
        const active = option.code === language;
        const href = resolveHref ? resolveHref(option.code) : undefined;
        return (
          <Fragment key={option.code}>
            {index > 0 && (
              <span aria-hidden="true" className="text-white/30">
                ·
              </span>
            )}
            <a
              href={href ?? "#"}
              aria-current={active ? "true" : undefined}
              onClick={(event) => {
                // Keep the anchor semantics (real href, pointer cursor, right-click) but
                // navigate via onLanguageChange so the server-rendered localized chrome
                // is reloaded in the new language.
                event.preventDefault();
                onLanguageChange(option.code);
              }}
              className={
                active
                  ? "cursor-pointer text-white"
                  : "cursor-pointer text-white/85 transition-colors hover:text-white"
              }
            >
              {option.code.toUpperCase()}
            </a>
          </Fragment>
        );
      })}
    </nav>
  );
}
